import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { PROJECTS } from '@/data/projects';
import type { Project } from '@/types/entities';

interface ApiProject {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  /** Task 201 D4 — taxonomy aliases. */
  aliases?: string[] | null;
  /** Task 201 D4 — taxonomy keywords. */
  keywords?: string[] | null;
  /** Task 201 D4 — external refs (Jira, GitHub, Slack). */
  externalRefs?: {
    jiraProjectKey?: string;
    githubRepos?: string[];
    slackChannels?: string[];
  } | null;
  billable: boolean;
  clientId: string;
  orgId: string;
  createdAt: string;
  systemOwned: boolean;
  archivedAt?: string | null;
  classifiedEventCount?: number;
}

interface UseProjectsOptions {
  /**
   * Include archived projects in the response. Default false — archived rows
   * are hidden unless the admin opts in via the "Show archived" toggle.
   */
  includeArchived?: boolean;
}

interface UseProjectsResult {
  data: readonly Project[];
  isLoading: boolean;
}

export function useProjects(opts: UseProjectsOptions = {}): UseProjectsResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;
  const includeArchived = opts.includeArchived ?? false;

  const query = useQuery({
    queryKey: ['projects', orgId, { includeArchived }],
    queryFn: async () => {
      const token = await getToken();
      const url = includeArchived
        ? `/api/v1/orgs/${orgId}/projects?includeArchived=true`
        : `/api/v1/orgs/${orgId}/projects`;
      const res = await apiFetch<{
        projects: Array<{
          id: string;
          clientId: string | null;
          name: string;
          slug: string;
          description?: string | null;
          aliases?: string[] | null;
          keywords?: string[] | null;
          externalRefs?: {
            jiraProjectKey?: string;
            githubRepos?: string[];
            slackChannels?: string[];
          } | null;
          billable: boolean;
          createdAt: string;
          systemOwned: boolean;
          archivedAt?: string | null;
          classifiedEventCount?: number;
          // Task 131 D6: the list endpoint includes `client: true`; we only
          // need the parent slug to drive the rule-target filter.
          client?: { slug: string } | null;
        }>;
      }>(url, token);
      return res.projects.map(
        (p): Project => ({
          id: p.id,
          clientId: p.clientId,
          name: p.name,
          slug: p.slug,
          clientSlug: p.client?.slug,
          description: p.description ?? null,
          aliases: p.aliases ?? null,
          keywords: p.keywords ?? null,
          externalRefs: p.externalRefs ?? null,
          type: p.billable ? 'billable' : 'general',
          createdAt: p.createdAt,
          systemOwned: p.systemOwned,
          archivedAt: p.archivedAt ?? null,
          classifiedEventCount: p.classifiedEventCount ?? 0,
        }),
      );
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) return { data: PROJECTS, isLoading: false };
  return { data: query.data ?? [], isLoading: query.isLoading };
}

// ── Mutation hooks ──────────────────────────────────────────────────────────
// Admin-only project CRUD. Each mutation invalidates both the projects list
// and the clients list (the latter embeds projects), and the rules list
// (archive/delete cascade-pauses rules). In mockup mode the mutation is a
// no-op — the fixture data is immutable.

export function useCreateProject() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body: {
      name: string;
      clientId: string;
      slug?: string;
      description?: string | null;
      billable?: boolean;
    }) => {
      if (mockupMode) return undefined as unknown as ApiProject;
      const token = await getToken();
      return apiFetch<ApiProject>(`/api/v1/orgs/${currentOrg.id}/projects`, token, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
    },
  });
}

export function useUpdateProject() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      slug?: string;
      description?: string | null;
      billable?: boolean;
      /** Task 201 D4 — taxonomy aliases. */
      aliases?: string[] | null;
      /** Task 201 D4 — taxonomy keywords. */
      keywords?: string[] | null;
      /** Task 201 D4 — external refs. */
      externalRefs?: {
        jiraProjectKey?: string;
        githubRepos?: string[];
        slackChannels?: string[];
      } | null;
    }) => {
      if (mockupMode) return undefined as unknown as ApiProject;
      const token = await getToken();
      return apiFetch<ApiProject>(`/api/v1/orgs/${currentOrg.id}/projects/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
    },
  });
}

export function useDeleteProject() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (mockupMode) return undefined;
      const token = await getToken();
      return apiFetch<undefined>(`/api/v1/orgs/${currentOrg.id}/projects/${id}`, token, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}

export function useArchiveProject() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (mockupMode) return undefined as unknown as ApiProject;
      const token = await getToken();
      return apiFetch<ApiProject>(`/api/v1/orgs/${currentOrg.id}/projects/${id}/archive`, token, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}

export function useRestoreProject() {
  const { currentOrg, mockupMode } = useAppContext();
  const getToken = useToken();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (mockupMode) return undefined as unknown as ApiProject;
      const token = await getToken();
      return apiFetch<ApiProject>(`/api/v1/orgs/${currentOrg.id}/projects/${id}/restore`, token, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['clients', currentOrg.id] });
      void qc.invalidateQueries({ queryKey: ['rules', currentOrg.id] });
    },
  });
}
